import { Schema } from "effect"
import { Actor, Hold, RecordMeta } from "../common"
import {
  AreaId,
  ArtifactId,
  GoalId,
  IntegrationId,
  KpiId,
  Markdown,
  Millis,
  Rank,
  RunId,
  ShortRef,
  Slug,
  Why,
} from "../ids"

// #region kpi
export const Comparator = Schema.Literals([">=", ">", "<=", "<", "=="])
/** A threshold on one KPI. Targets live on goals and bets, never on the KPI (a KPI can serve two goals). */
export const MetricCondition = Schema.Struct({
  kpiId: KpiId,
  op: Comparator,
  value: Schema.Finite,
  minN: Schema.NullOr(Schema.Int), // minimum sample before judging (low-volume reality)
})

export const KpiSource = Schema.TaggedUnion({
  manual: {},
  /**
   * Never filled: Minga builds no integrations (decision 0097). A KPI measured from an outside source is `manual` with a
   * recipe that a worker follows; kept so old schemas decode.
   */
  connector: { integrationId: IntegrationId, query: Schema.JsonObject },
  /** Derived, e.g. conversion = paid / trials. */
  formula: { expression: Schema.String, inputs: Schema.Array(KpiId) },
})

/**
 * Decision 0097: how a KPI's number is fetched, in plain words (source, query or steps, which tool or connection,
 * when). Agents and people write it; a worker whose item names the KPI follows it with its own tools (CLIs, MCP
 * servers, keys on its runner) and records the values with `record_kpi`. Minga never runs it itself.
 */
export const KpiRecipe = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4_000))

/** An entity, not a graph node: rendered as chips/sparklines inside goal nodes and area headers. */
export const Kpi = Schema.Struct({
  ...RecordMeta,
  id: KpiId,
  ref: ShortRef, // K-3
  areaId: Schema.NullOr(AreaId), // null = project-level
  key: Slug, // trial_starts_organic → "trial-starts-organic"
  name: Schema.String,
  unit: Schema.String, // "users", "%", "€"
  direction: Schema.Literals(["up", "down"]), // which way is good
  role: Schema.Literals(["input", "lagging", "health"]),
  source: KpiSource,
  cadence: Schema.Literals(["hourly", "daily", "weekly"]),
  /** Data latency (GSC ~3 d, ASC ~2 d): samples inside it are provisional and never auto-judged. */
  latencyDays: Schema.Int,
  /** ASC hides counts < 5: below this value show "<n" and never auto-judge. */
  minReportable: Schema.NullOr(Schema.Int),
  /** Metric tree: this KPI feeds those (input KPI → goal KPI). Not edges. */
  rollsUpTo: Schema.Array(KpiId),
  baseline: Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis })),
  /** Latest sample, folded from KpiSampleRecorded. History lives in the kpi_samples projection. */
  latest: Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis, provisional: Schema.Boolean })),
  /** Decision 0097: the recipe (absent on KPIs defined before it; null = none). */
  recipe: Schema.optionalKey(Schema.NullOr(KpiRecipe)),
})

export const KpiSample = Schema.Struct({
  at: Millis, // the period the value describes, not when it was fetched
  value: Schema.Finite,
  fetchedAt: Millis,
  provisional: Schema.Boolean,
  n: Schema.NullOr(Schema.Int),
})

/**
 * How a KPI sample was measured (decision 0045): the query and where it ran, the tool call, a URL, or a note; or the run
 * / artifact that holds it. Required (≥ 1) when an agent or a worker records a sample; a person's sample may carry none.
 */
export const KpiEvidence = Schema.TaggedUnion({
  /** `source`: where it ran (posthog, stripe, sql, …); `query`: the query text as run. */
  query: {
    source: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
    query: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4_000)),
  },
  /** A tool call that returned the number: its name and (optionally) its input as text. */
  tool_call: {
    tool: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
    input: Schema.NullOr(Schema.String.check(Schema.isMaxLength(4_000))),
  },
  url: { url: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_000)), note: Schema.String },
  note: { text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4_000)) },
  run: { runId: RunId },
  artifact: { artifactId: ArtifactId },
})
export type KpiEvidence = typeof KpiEvidence.Type

/**
 * Decision 0097, the KPI history: one logged sample with who recorded it and how. `replaced`: a later sample for the
 * same period took its place (people and agents correct a number by recording the period again).
 */
export const KpiHistorySample = Schema.Struct({
  ...KpiSample.fields,
  /** When it was recorded (the event's time); `at` is the period it describes. */
  recordedAt: Millis,
  by: Actor,
  evidence: Schema.Array(KpiEvidence),
  replaced: Schema.Boolean,
})
export type KpiHistorySample = typeof KpiHistorySample.Type

/**
 * What happened around a KPI (decision 0097, goalposts P3), from the event log: work planned, started or finished under
 * the goals that use it or the bets on it, releases, merges, verdicts, decisions, target and KPI changes. Placed by
 * time, never a claim of cause.
 */
export const KpiMarkerKind = Schema.Literals([
  "planned", //  a top-level item created for a goal that uses the KPI, or a bet on it
  "started", //  such an item moved to Now
  "done", //     such an item finished
  "release", //  a release (any) finished
  "merged", //   a PR merged for work under such an item
  "verdict", //  a bet on the KPI was judged
  "decision", // a decision comment on such a goal or item
  "target", //   a goal's target or definition changed
  "goal", //     a goal's status changed
  "kpi", //      the KPI itself changed (its recipe, unit, …)
])
export type KpiMarkerKind = typeof KpiMarkerKind.Type
export const KpiMarker = Schema.Struct({
  at: Millis,
  seq: Schema.Int,
  kind: KpiMarkerKind,
  /** The record it is about (DIC-12, G-2, K-3), or null. */
  ref: Schema.NullOr(Schema.String),
  /** The record's title, or what changed, in a few words. */
  title: Schema.String,
  note: Schema.NullOr(Schema.String),
  by: Actor,
})
export type KpiMarker = typeof KpiMarker.Type
export const KpiHistory = Schema.Struct({
  kpiId: KpiId,
  /** Oldest first: every logged sample whose period is in the window, replaced ones included. */
  samples: Schema.Array(KpiHistorySample),
  /** Oldest first, at most {@link KPI_MARKERS_MAX}. */
  markers: Schema.Array(KpiMarker),
})
export type KpiHistory = typeof KpiHistory.Type
export const KPI_MARKERS_MAX = 500
// #endregion kpi

// #region goal
/** Shared by goals and items: what every view and the context assembler need. */
export const NodeBase = {
  ...RecordMeta,
  ref: ShortRef,
  title: Schema.String,
  /** One or two sentences: why this exists. Required; shown in the inspector, re-injected into every run's context. */
  why: Why,
  body: Markdown,
  rank: Rank, // priority among siblings; people drag it
  hold: Hold,
  tags: Schema.Array(Slug), // memory triggers and filters
} as const

/**
 * Judged (decision 0035): a target on a KPI by a deadline, or a person judges it (`target: null`, with `criteria`, the
 * old milestone goals). Health is the latest `update` comment on it. Goals stay per project (company goals live in a
 * Company project).
 */
export const Goal = Schema.Struct({
  ...NodeBase,
  id: GoalId,
  status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
  deadline: Schema.NullOr(Millis),
  /** "paid_users >= 100"; null = a person judges it. */
  target: Schema.NullOr(MetricCondition),
  /** What the person checks when judging (from an old milestone's definition of done). */
  criteria: Schema.Array(Schema.String),
  /** Supporting KPIs shown in the goal node (input KPIs rolled up from areas). */
  kpiIds: Schema.Array(KpiId),
})
export type Goal = typeof Goal.Type
// #endregion goal

// #region bet-and-horizon
/** v1 initiatives' planning horizon: kept for legacy events and items decoded from v1 records. */
export const Horizon = Schema.Literals(["now", "next", "later"])
export const BetVerdictValue = Schema.Literals([
  "worked",
  "promising",
  "inconclusive",
  "no_effect",
  "harmful",
])
// #endregion bet-and-horizon
